import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Attach bearer token fallback if cookie not available (cross-site)
api.interceptors.request.use((config) => {
  const tok = localStorage.getItem("session_token");
  if (tok) config.headers.Authorization = `Bearer ${tok}`;
  return config;
});

// ---- Auto-retry after top-up ------------------------------------------
// When a request dies with 402 INSUFFICIENT_CREDITS we don't reject the
// caller's promise straight away — we PARK it. If the founder completes the
// inline top-up (TopUpModal fires "clinq:credits-topped-up"), the request is
// replayed with the same config and the original caller's promise resolves
// with the retried response, so the blocked action finishes exactly as if it
// had never failed (success toasts, UI updates, downloads all run).
// The parked promise rejects with the original 402 (marked isCreditShortage
// so call sites stay silent) if the founder ignores the toast for 45s,
// closes the top-up modal without paying, or a newer 402 supersedes it.

let _parked = null; // { config, err, resolve, reject, timer }

function _rejectParked() {
  if (!_parked) return;
  const { err, reject, timer } = _parked;
  _parked = null;
  clearTimeout(timer);
  reject(err);
}

function _parkRequest(err) {
  return new Promise((resolve, reject) => {
    _rejectParked(); // only the latest blocked action is retried
    const timer = setTimeout(() => {
      if (_parked && _parked.err === err) _rejectParked();
    }, 45000);
    _parked = { config: err.config, err, resolve, reject, timer };
  });
}

// Founder opened the top-up modal — hold the parked action for as long as
// the modal stays open (M-Pesa payments can take minutes).
window.addEventListener("clinq:topup", () => {
  if (_parked) clearTimeout(_parked.timer);
});

// Modal closed without a successful payment — give the caller its error back.
window.addEventListener("clinq:topup-closed", _rejectParked);

// Payment cleared — replay the blocked request and settle the original promise.
window.addEventListener("clinq:credits-topped-up", () => {
  if (!_parked) return;
  const { config, resolve, reject, timer } = _parked;
  _parked = null;
  clearTimeout(timer);
  toast.loading("Credits added — finishing your action…", { id: "insufficient-credits" });
  api.request(config).then(
    (res) => { toast.dismiss("insufficient-credits"); resolve(res); },
    (e) => { toast.dismiss("insufficient-credits"); reject(e); },
  );
});

// Global 402 handler — every "not enough credits" error, anywhere in the
// app, surfaces one toast with a "Buy credits" CTA. The `id` dedupes
// repeat toasts; `err.isCreditShortage` lets call sites skip their own
// duplicate error toast. Navigation goes through a window event so this
// module (outside the Router) can still trigger SPA navigation.
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const d = err?.response?.data?.detail;
    if (err?.response?.status === 402 && d && typeof d === "object" && d.code === "INSUFFICIENT_CREDITS") {
      err.isCreditShortage = true;
      toast.error(d.message || "You don't have enough credits for this action.", {
        id: "insufficient-credits",
        duration: 10000,
        action: {
          label: "Buy credits",
          onClick: () => window.dispatchEvent(new CustomEvent("clinq:topup", {
            detail: { needed: d.needed, balance: d.balance },
          })),
        },
      });
      // Park the caller's promise for a potential auto-retry after top-up.
      return _parkRequest(err);
    }
    return Promise.reject(err);
  }
);
